import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUserPlus, FaEdit, FaTrash, FaCheck, FaTimes, 
  FaUserShield, FaUserTie, FaEye 
} from 'react-icons/fa';
import './UserManagement.css';

// Account changes need the service role, so they go through the admin-users Edge Function.
const invokeAdminUsers = async (body) => {
  const { data, error } = await supabase.functions.invoke('admin-users', { body });
  if (error) {
    let message = 'Request failed';
    try {
      message = (await error.context.json()).error || message;
    } catch (e) {
      // keep the generic message
    }
    throw new Error(message);
  }
  return data;
};

const UserManagement = () => {
  const { isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    full_name: '',
    role: 'viewer',
    is_active: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isSuperAdmin) {
      fetchUsers();
    }
  }, [isSuperAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_users')
        .select('*, creator:admin_users!created_by(full_name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data.map(u => ({ ...u, created_by_name: u.creator?.full_name })));
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({
      username: '',
      password: '',
      email: '',
      full_name: '',
      role: 'viewer',
      is_active: true
    });
    setErrors({});
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setModalMode('edit');
    setSelectedUser(user);
    setFormData({
      full_name: user.full_name,
      email: user.email,
      role: user.role,
      is_active: user.is_active
    });
    setErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      password: '',
      email: '',
      full_name: '',
      role: 'viewer',
      is_active: true
    });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (modalMode === 'create') {
      if (!formData.username.trim()) {
        newErrors.username = 'Username is required';
      } else if (formData.username.length < 3) {
        newErrors.username = 'Username must be at least 3 characters';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (modalMode === 'create') {
        await invokeAdminUsers({ action: 'create', ...formData });
        alert('Admin user created successfully!');
      } else {
        await invokeAdminUsers({ action: 'update', id: selectedUser.id, ...formData });
        alert('Admin user updated successfully!');
      }

      fetchUsers();
      closeModal();
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error.message || 'Failed to save user');
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to deactivate ${userName}?`)) {
      return;
    }

    try {
      await invokeAdminUsers({ action: 'deactivate', id: userId });
      alert('User deactivated successfully!');
      fetchUsers();
    } catch (error) {
      console.error('Error deactivating user:', error);
      alert(error.message || 'Failed to deactivate user');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super-admin':
        return <FaUserShield />;
      case 'admin':
        return <FaUserTie />;
      case 'viewer':
        return <FaEye />;
      default:
        return <FaUserTie />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super-admin':
        return '#e74c3c';
      case 'admin':
        return '#3498db';
      case 'viewer':
        return '#95a5a6';
      default:
        return '#95a5a6';
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Only super-administrators can access user management.</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="user-management">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>Manage admin users and their permissions</p>
        </div>
        <button className="btn-create" onClick={openCreateModal}>
          <FaUserPlus /> Create New User
        </button>
      </div>

      <div className="users-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : (
          <div className="users-grid">
            {users.map((user) => (
              <motion.div
                key={user.id}
                className={`user-card ${!user.is_active ? 'inactive' : ''}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="user-card-header">
                  <div className="user-avatar" style={{ background: getRoleColor(user.role) }}>
                    {getRoleIcon(user.role)}
                  </div>
                  <div className="user-info">
                    <h3>{user.full_name}</h3>
                    <p className="user-username">@{user.username}</p>
                  </div>
                  {!user.is_active && (
                    <span className="inactive-badge">Inactive</span>
                  )}
                </div>

                <div className="user-card-body">
                  <div className="user-detail">
                    <strong>Email:</strong>
                    <span>{user.email}</span>
                  </div>
                  <div className="user-detail">
                    <strong>Role:</strong>
                    <span className="role-badge" style={{ background: getRoleColor(user.role) }}>
                      {user.role}
                    </span>
                  </div>
                  <div className="user-detail">
                    <strong>Created:</strong>
                    <span>{new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                  {user.created_by_name && (
                    <div className="user-detail">
                      <strong>Created By:</strong>
                      <span>{user.created_by_name}</span>
                    </div>
                  )}
                  {user.last_login && (
                    <div className="user-detail">
                      <strong>Last Login:</strong>
                      <span>{new Date(user.last_login).toLocaleString()}</span>
                    </div>
                  )}
                </div>

                <div className="user-card-actions">
                  <button
                    className="btn-edit"
                    onClick={() => openEditModal(user)}
                  >
                    <FaEdit /> Edit
                  </button>
                  {user.is_active && (
                    <button
                      className="btn-deactivate"
                      onClick={() => handleDeactivate(user.id, user.full_name)}
                    >
                      <FaTrash /> Deactivate
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit User Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>
                  {modalMode === 'create' ? 'Create New Admin User' : 'Edit Admin User'}
                </h2>
                <button className="btn-close" onClick={closeModal}>
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="modal-body">
                {modalMode === 'create' && (
                  <>
                    <div className="form-group">
                      <label htmlFor="username">Username *</label>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className={errors.username ? 'error' : ''}
                      />
                      {errors.username && (
                        <span className="error-message">{errors.username}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password *</label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={errors.password ? 'error' : ''}
                      />
                      {errors.password && (
                        <span className="error-message">{errors.password}</span>
                      )}
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label htmlFor="full_name">Full Name *</label>
                  <input
                    type="text"
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    className={errors.full_name ? 'error' : ''}
                  />
                  {errors.full_name && (
                    <span className="error-message">{errors.full_name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="role">Role *</label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                    <option value="super-admin">Super Admin</option>
                  </select>
                  <div className="role-descriptions">
                    <p><strong>Viewer:</strong> Can only view applications and data (read-only access)</p>
                    <p><strong>Admin:</strong> Can view, update status, add notes, and manage applications</p>
                    <p><strong>Super Admin:</strong> Full access including user management</p>
                  </div>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                    />
                    <span>Active</span>
                  </label>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-cancel" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-save">
                    <FaCheck /> {modalMode === 'create' ? 'Create User' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
